import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  BackwardIcon,
  ArrowPathIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import rumService from '../../services/api/rum.service';
import toast from 'react-hot-toast';

interface SessionInfo {
  id: string;
  session_id: string;
  user_id?: string;
  platform: string;
  device_type?: string;
  device_model?: string;
  browser_name?: string;
  browser_version?: string;
  os_name?: string;
  os_version?: string;
  screen_width?: number;
  screen_height?: number;
  viewport_width?: number;
  viewport_height?: number;
  country?: string;
  city?: string;
  ip_address?: string;
  started_at: string;
  ended_at?: string;
  duration_ms?: number;
  page_view_count: number;
  event_count: number;
  error_count: number;
  has_recording: boolean;
}

interface TimelineEvent {
  id: string;
  type: 'pageView' | 'click' | 'scroll' | 'error' | 'networkRequest';
  timestamp: number;
  title: string;
  details?: any;
  x?: number;
  y?: number;
}

interface HeatmapPoint {
  x: number;
  y: number;
  intensity: number;
}

export default function SessionDetailsPage() {
  const { t } = useTranslation(['rum', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Close modal and go back
  const handleClose = () => {
    // Go back in history if possible, otherwise go to RUM page
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/analytics/rum');
    }
  };

  const [session, setSession] = useState<SessionInfo | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [heatmapPoints, setHeatmapPoints] = useState<HeatmapPoint[]>([]);
  const [loading, setLoading] = useState(true);

  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(120000); // 2 minutes default
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const animationFrameRef = useRef<number>();
  const lastTimeRef = useRef<number>(Date.now());

  // Handle ESC key to close
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, []);

  // Prevent scroll on body when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (id) {
      loadSessionData();
    }
  }, [id]);

  useEffect(() => {
    if (isPlaying) {
      const animate = () => {
        const now = Date.now();
        const delta = (now - lastTimeRef.current) * playbackSpeed;
        lastTimeRef.current = now;

        setCurrentTime((prev) => {
          const newTime = prev + delta;
          if (newTime >= duration) {
            setIsPlaying(false);
            return duration;
          }
          return newTime;
        });

        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animationFrameRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    } else {
      lastTimeRef.current = Date.now();
    }
  }, [isPlaying, playbackSpeed, duration]);

  const loadSessionData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const sessionData = await rumService.getSession(id);

      setSession({
        id: sessionData.id,
        session_id: sessionData.session_id,
        user_id: sessionData.user_id,
        platform: sessionData.platform,
        device_type: sessionData.device_type,
        device_model: sessionData.device_model,
        browser_name: sessionData.browser_name,
        browser_version: sessionData.browser_version,
        os_name: sessionData.os_name,
        os_version: sessionData.os_version,
        screen_width: sessionData.screen_width,
        screen_height: sessionData.screen_height,
        viewport_width: sessionData.viewport_width,
        viewport_height: sessionData.viewport_height,
        country: sessionData.country,
        city: sessionData.city,
        ip_address: sessionData.ip_address,
        started_at: sessionData.started_at,
        ended_at: sessionData.ended_at,
        duration_ms: sessionData.duration_ms || 120000,
        page_view_count: 0,
        event_count: 0,
        error_count: 0,
        has_recording: true,
      });

      const durationValue = sessionData.duration_ms || 120000;
      setDuration(durationValue);

      // Generate mock timeline events
      const events: TimelineEvent[] = [
        {
          id: '1',
          type: 'pageView',
          timestamp: 0,
          title: '/home',
          details: { url: 'https://example.com/home', loadTime: 1200 },
        },
        {
          id: '2',
          type: 'click',
          timestamp: 5000,
          title: 'Clicou no botão "Começar"',
          details: { elementId: 'cta-button', text: 'Começar' },
          x: 45,
          y: 30,
        },
        {
          id: '3',
          type: 'scroll',
          timestamp: 12000,
          title: 'Rolou até 45%',
          details: { depth: 45 },
        },
        {
          id: '4',
          type: 'pageView',
          timestamp: 18000,
          title: '/produtos',
          details: { url: 'https://example.com/produtos', loadTime: 850 },
        },
        {
          id: '5',
          type: 'click',
          timestamp: 25000,
          title: 'Clicou no card do produto',
          details: { productId: '123', name: 'Produto A' },
          x: 60,
          y: 50,
        },
        {
          id: '6',
          type: 'error',
          timestamp: 32000,
          title: 'TypeError: Não é possível ler propriedade',
          details: { message: "Não é possível ler propriedade 'length' de undefined", file: 'app.js', line: 42 },
        },
        {
          id: '7',
          type: 'networkRequest',
          timestamp: 40000,
          title: 'GET /api/produtos',
          details: { status: 200, duration: 185 },
        },
        {
          id: '8',
          type: 'click',
          timestamp: 50000,
          title: 'Clicou no menu de navegação',
          details: { menu: 'Produtos' },
          x: 20,
          y: 15,
        },
        {
          id: '9',
          type: 'scroll',
          timestamp: 65000,
          title: 'Rolou até 80%',
          details: { depth: 80 },
        },
        {
          id: '10',
          type: 'click',
          timestamp: 75000,
          title: 'Clicou em "Adicionar ao Carrinho"',
          details: { productId: '123' },
          x: 70,
          y: 60,
        },
      ];

      setTimeline(events);

      // Generate heatmap points from click events
      const clickEvents = events.filter(e => e.type === 'click' && e.x && e.y);
      const points: HeatmapPoint[] = clickEvents.map(e => ({
        x: e.x!,
        y: e.y!,
        intensity: 0.7 + Math.random() * 0.3,
      }));

      // Add some random points for better visualization
      for (let i = 0; i < 15; i++) {
        points.push({
          x: Math.random() * 100,
          y: Math.random() * 100,
          intensity: Math.random() * 0.6 + 0.2,
        });
      }

      setHeatmapPoints(points);
    } catch (error) {
      console.error('Failed to load session:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSkip = (seconds: number) => {
    setCurrentTime(prev => Math.max(0, Math.min(duration, prev + seconds * 1000)));
  };

  const handleRestart = () => {
    setCurrentTime(0);
    setIsPlaying(true);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    setCurrentTime(percentage * duration);
    // Pause when seeking
    if (isPlaying) {
      setIsPlaying(false);
    }
  };

  const handleEventClick = (event: TimelineEvent) => {
    setCurrentTime(event.timestamp);
  };

  const handleDeleteRecording = async () => {
    if (!session?.session_id) return;

    try {
      setIsDeleting(true);
      await rumService.deleteRecording(session.session_id);
      // Close modal and navigate back after successful deletion
      setShowDeleteModal(false);
      handleClose();
    } catch (error) {
      console.error('Failed to delete recording:', error);
      toast.error(t('rum:errors.deleteRecordingFailed') || 'Erro ao deletar gravação. Por favor, tente novamente.', {
        duration: 5000,
        icon: '❌',
      });
      setIsDeleting(false);
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'pageView':
        return (
          <div className="p-1.5 bg-blue-500/20 rounded">
            <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      case 'click':
        return (
          <div className="p-1.5 bg-purple-500/20 rounded">
            <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
          </div>
        );
      case 'scroll':
        return (
          <div className="p-1.5 bg-green-500/20 rounded">
            <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="p-1.5 bg-red-500/20 rounded">
            <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        );
      case 'networkRequest':
        return (
          <div className="p-1.5 bg-yellow-500/20 rounded">
            <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getHeatmapColor = (intensity: number): string => {
    if (intensity > 0.7) return 'rgba(220, 38, 38, 0.6)'; // red
    if (intensity > 0.4) return 'rgba(249, 115, 22, 0.5)'; // orange
    return 'rgba(234, 179, 8, 0.4)'; // yellow
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="text-center flex flex-col items-center justify-center">
          <div className="relative w-16 h-16 mb-6">
            <div className="absolute inset-0 w-16 h-16 border-4 border-gray-800 rounded-full"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-cyan-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-400 text-lg font-medium">Carregando sessão...</p>
          <p className="mt-2 text-gray-600 text-sm">Preparando replay da sessão</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="text-center">
          <p className="text-gray-400 text-lg">Sessão não encontrada</p>
          <button
            onClick={handleClose}
            className="mt-4 px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const progress = (currentTime / duration) * 100;
  const activeEvents = timeline.filter(e => e.timestamp <= currentTime);
  const nextEvent = timeline.find(e => e.timestamp > currentTime);

  return (
    <div
      className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex flex-col transition-opacity duration-300"
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-b from-gray-900/90 to-black/90 border-b border-gray-800">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors group"
            title="Fechar (ESC)"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 group-hover:text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Reprodução de Sessão</h1>
            <p className="text-sm text-gray-400">
              <span className="text-cyan-400 font-mono">{session.session_id.substring(0, 8)}</span>
              {session.user_id && <span className="ml-3">Usuário: {session.user_id}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowHeatmap(!showHeatmap)}
            className={`px-4 py-2 rounded-lg transition-all ${
              showHeatmap
                ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg shadow-red-500/30'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {showHeatmap ? '🔥 Mapa de Calor Ativo' : 'Mostrar Mapa de Calor'}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2"
            title="Deletar gravação"
          >
            <TrashIcon className="w-5 h-5" />
            <span>Deletar</span>
          </button>
          <div className="text-right">
            <p className="text-sm text-gray-400">Duração</p>
            <p className="text-white font-semibold">{formatTime(session.duration_ms || 0)}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Player Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-auto">
          <div className="relative w-full max-w-6xl">
            {/* Simulated Browser Window */}
            <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-t-lg p-3 border border-gray-700">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="flex-1 ml-4 bg-gray-900 rounded px-3 py-1">
                  <p className="text-xs text-gray-500">https://example.com/home</p>
                </div>
              </div>
            </div>

            {/* Screen Content */}
            <div className="relative bg-white aspect-[16/10] border-x border-b border-gray-700 overflow-hidden">
              {/* Simulated Page Content */}
              <div className="absolute inset-0 p-8">
                <div className="space-y-6">
                  <div className="h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg animate-pulse"></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="h-32 bg-gray-200 rounded-lg"></div>
                    <div className="h-32 bg-gray-200 rounded-lg"></div>
                    <div className="h-32 bg-gray-200 rounded-lg"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-300 rounded w-full"></div>
                    <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="h-48 bg-gray-100 rounded-lg p-4">
                      <div className="h-full bg-gray-300 rounded"></div>
                    </div>
                    <div className="h-48 bg-gray-100 rounded-lg p-4">
                      <div className="h-full bg-gray-300 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Heatmap Overlay */}
              {showHeatmap && (
                <div className="absolute inset-0 pointer-events-none">
                  {heatmapPoints.map((point, index) => (
                    <div
                      key={index}
                      className="absolute animate-pulse"
                      style={{
                        left: `${point.x}%`,
                        top: `${point.y}%`,
                        width: '80px',
                        height: '80px',
                        transform: 'translate(-50%, -50%)',
                        background: `radial-gradient(circle, ${getHeatmapColor(point.intensity)} 0%, transparent 70%)`,
                        filter: 'blur(20px)',
                        opacity: point.intensity,
                      }}
                    ></div>
                  ))}
                </div>
              )}

              {/* Custom Cursor */}
              <div
                className="absolute w-6 h-6 pointer-events-none transition-all duration-200"
                style={{
                  left: `${30 + (currentTime / duration) * 40}%`,
                  top: `${20 + Math.sin(currentTime / 1000) * 30}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <svg className="w-6 h-6 text-cyan-500 drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5 3l14 9-5.5 1.5L11 19z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="w-full max-w-6xl mt-6 space-y-4">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div
                className="relative h-2 bg-gray-800 rounded-full cursor-pointer group"
                onClick={handleSeek}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                ></div>

                {/* Event Markers */}
                {timeline.map((event) => (
                  <div
                    key={event.id}
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full cursor-pointer"
                    style={{
                      left: `${(event.timestamp / duration) * 100}%`,
                      backgroundColor:
                        event.type === 'error'
                          ? '#ef4444'
                          : event.type === 'click'
                          ? '#a855f7'
                          : event.type === 'pageView'
                          ? '#3b82f6'
                          : '#10b981',
                    }}
                    title={event.title}
                  ></div>
                ))}

                {/* Playhead */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg transition-all"
                  style={{ left: `${progress}%` }}
                ></div>
              </div>

              <div className="flex justify-between text-sm text-gray-400">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleRestart}
                  className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="Reiniciar"
                >
                  <ArrowPathIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                </button>
                <button
                  onClick={() => handleSkip(-10)}
                  className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="Voltar 10s"
                >
                  <BackwardIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                </button>
                <button
                  onClick={handlePlayPause}
                  className="p-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-lg transition-all shadow-lg shadow-cyan-500/30"
                >
                  {isPlaying ? (
                    <PauseIcon className="w-6 h-6 text-white" />
                  ) : (
                    <PlayIcon className="w-6 h-6 text-white" />
                  )}
                </button>
                <button
                  onClick={() => handleSkip(10)}
                  className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
                  title="Avançar 10s"
                >
                  <ForwardIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                </button>
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
                  title={isMuted ? 'Ativar som' : 'Silenciar'}
                >
                  {isMuted ? (
                    <SpeakerXMarkIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                  ) : (
                    <SpeakerWaveIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                  )}
                </button>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-400">Velocidade:</span>
                  {[0.5, 1, 1.5, 2].map((speed) => (
                    <button
                      key={speed}
                      onClick={() => setPlaybackSpeed(speed)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        playbackSpeed === speed
                          ? 'bg-cyan-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Events Timeline */}
        <div className="w-96 bg-gray-900 border-l border-gray-800 flex flex-col">
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">Timeline de Eventos</h2>
            <p className="text-sm text-gray-400 mt-1">
              {activeEvents.length} de {timeline.length} eventos
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {timeline.map((event) => {
              const isPast = event.timestamp <= currentTime;
              const isActive = nextEvent?.id === event.id;

              return (
                <button
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-cyan-600 shadow-lg shadow-cyan-500/30'
                      : isPast
                      ? 'bg-gray-800 hover:bg-gray-750'
                      : 'bg-gray-800/40 hover:bg-gray-800/60'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">{getEventIcon(event.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium uppercase tracking-wide ${
                          isPast ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {event.type}
                        </span>
                        <span className={`text-xs ${isPast ? 'text-gray-400' : 'text-gray-600'}`}>
                          {formatTime(event.timestamp)}
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${
                        isActive ? 'text-white' : isPast ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {event.title}
                      </p>
                      {event.details && (
                        <p className={`text-xs mt-1 ${
                          isActive ? 'text-cyan-200' : isPast ? 'text-gray-500' : 'text-gray-700'
                        }`}>
                          {JSON.stringify(event.details, null, 2).substring(0, 60)}...
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Session Info */}
          <div className="p-4 border-t border-gray-800 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-gray-500">Dispositivo</p>
                <p className="text-white font-medium capitalize">{session.device_type || 'Desktop'}</p>
              </div>
              <div>
                <p className="text-gray-500">Navegador</p>
                <p className="text-white font-medium">{session.browser_name}</p>
              </div>
              <div>
                <p className="text-gray-500">Sistema</p>
                <p className="text-white font-medium">{session.os_name}</p>
              </div>
              <div>
                <p className="text-gray-500">Localização</p>
                <p className="text-white font-medium">{session.country || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 border border-gray-700">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Deletar Gravação
                </h3>
                <p className="text-gray-400 text-sm">
                  Tem certeza que deseja deletar esta gravação de sessão? Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>

            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <p className="text-xs text-gray-500 mb-1">ID da Sessão</p>
              <p className="text-white font-mono text-sm">{session?.session_id}</p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteRecording}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Deletando...</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>Deletar Gravação</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
